import {yupResolver} from '@hookform/resolvers/yup';
import {round} from 'lodash';
import moment from 'moment';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {
  ActivityIndicator,
  Dimensions,
  FlatList,
  Modal,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import * as yup from 'yup';
import {ShowToast, retrieveSetting} from '../Common/common';
import BTComboBox from '../Components/BTComboBox';
import {Col, Row} from '../Components/BTGrid';
import {TextInput} from '../Components/BTTextInput';
import {useGetEmployeesQuery} from '../redux/services/emmemployeeApi';
import {useGetPOAOutletsQuery} from '../redux/services/poaoutletApi';
import {
  useInsertPOEBillMutation,
  useUpdatePOEBillMutation,
} from '../redux/services/poebillApi';
import {useGetPOEOrderByTableQuery} from '../redux/services/poeorderApi';
import {useGetPOMTablesQuery} from '../redux/services/pomtableApi';
import {RootState} from '../redux/store';
import styles from '../styles/Styles';
import {
  borderRadius,
  colors,
  shadows,
  spacing,
  typography,
} from '../theme/theme';

import T from '../Components/T';
import { tp } from '../utils/translateText';
const buttonStyles = StyleSheet.create({
  buttonRowContainer: {
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.sm,
    backgroundColor: colors.white,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
    ...shadows.sm,
  },
  iconButton: {
    backgroundColor: colors.primary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    borderRadius: borderRadius.base,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs / 2,
    ...shadows.sm,
  },
  iconButtonText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    color: colors.white,
    textAlign: 'center',
    marginTop: 2,
  },
});

const validationSchema = yup.object({
  bl_table: yup.number().moreThan(0, 'Table is required'),
  bl_waiter: yup.number().moreThan(0, 'Waiter is required'),
  bl_pax: yup
    .number()
    .typeError('Pax is required')
    .min(1, 'Pax should be >= 1')
    .max(10, 'Pax should be <= 10'),
  bl_mobile: yup
    .string()
    .required('Mobile is required')
    .max(10, 'Enter Correct Mobile No')
    .min(10, 'Enter Correct Mobile No'),
});

interface BillFooter {
  stotqty: string;
  stotamt: string;
  totdisc: string;
  tottax: string;
  roff: string;
  ntotamt: string;
}

interface BillQuery {
  cid: string;
  tbl: number;
  value: any;
}

const POSSBill = ({navigation, route}: UniversalProps) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const {data: outletData, isLoading: isLoadingOutlets} = useGetPOAOutletsQuery(
    gdata.company,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [defOutlet, setDefOutlet] = useState(13);
  const [isSplitBillOpen, setIsSplitBillOpen] = useState(false);
  const [splitBills, setSplitBills] = useState<any[]>([]);
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get('window').width,
  );

  // Update screen width on dimension changes
  useEffect(() => {
    const updateLayout = () => {
      const {width} = Dimensions.get('window');
      setScreenWidth(width);
    };

    updateLayout();
    const subscription = Dimensions.addEventListener('change', updateLayout);
    return () => subscription?.remove();
  }, []);

  // Initialize default outlet
  useEffect(() => {
    const retDefOutlet = async () => {
      const outlet = await retrieveSetting('defoutlet');
      setDefOutlet(Number(outlet) || 13);
    };
    retDefOutlet();
  }, []);

  // Format outlets for dropdown
  const outlets = useMemo(() => {
    return outletData?.map(value => ({
      label: value.ol_sname,
      id: value.ol_id,
      value: value,
    }));
  }, [outletData]);

  // Fetch tables data
  const {data, isLoading: isLoadingTables} = useGetPOMTablesQuery(
    {cid: gdata.company, olid: defOutlet}
  );

  const {data: employeeData, isLoading: isLoadingEmployees} =
    useGetEmployeesQuery(gdata.company);

  const [billQry, setBillQry] = useState<BillQuery>({
    cid: gdata.company,
    tbl: 0,
    value: undefined,
  });

  const {data: billData, isLoading: isLoadingBill} = useGetPOEOrderByTableQuery(
    {
      cid: billQry.cid,
      tbl: billQry.tbl,
    }
  );

  const [billfooter, setBillfooter] = useState<BillFooter>({
    stotqty: '0.00',
    stotamt: '0.00',
    totdisc: '0.00',
    tottax: '0.00',
    roff: '0.00',
    ntotamt: '0.00',
  });

  // Calculate bill details and totals
  const {billDetails, totals} = useMemo(() => {
    let totQty = 0;
    let totAmt = 0;

    const details =
      billData?.map((v: any, i: number) => {
        totQty += Number(v.od_qty);
        totAmt += round(Number(v.od_qty) * Number(v.od_rate), 2);

        return (
          <View key={i} style={styles.billItemRow}>
            <View style={{flex: 2, paddingRight: 8}}>
              <Text style={styles.billItemDescription}>{v.od_descr}</Text>
            </View>
            <View style={{width: 70, alignItems: 'flex-end'}}>
              <Text style={styles.billItemRate}>
                {Number(v.od_rate).toFixed(2)}
              </Text>
            </View>
            <View style={{width: 50, alignItems: 'center'}}>
              <Text style={styles.billItemQty}>{v.od_qty}</Text>
            </View>
            <View style={{width: 90, alignItems: 'flex-end'}}>
              <Text style={styles.billItemAmount}>
                {round(v.od_rate * v.od_qty, 2).toFixed(2)}
              </Text>
            </View>
          </View>
        );
      }) || [];

    return {billDetails: details, totals: {totQty, totAmt}};
  }, [billData]);

  // Handle route params for table selection
  useEffect(() => {
    if (route.params?.table) {
      handleTableClick(route.params?.table);
    }
  }, []);

  const initialValues: any = useMemo(
    () => ({
      bl_id: 0,
      bl_company: gdata.company,
      bl_outlet: defOutlet,
      bl_date:
        moment(gdata.today).format('YYYY/MM/DD') + moment().format(' HH:mm:ss'),
      bl_no: '',
      bl_checkin: '',
      bl_table: 0,
      bl_tablename: '',
      bl_waiter: 0,
      bl_pax: '',
      bl_mobile: '',
      bl_name: '',
      bl_address: '',
      bl_kotids: '',
      bl_kotnos: '',
      bl_roff: 0,
      bl_amt: 0,
      bl_flg: 0,
      bl_uid: gdata.user,
      bl_cdt: moment().format('YYYY/MM/DD HH:mm:ss'),
      bl_muid: gdata.user,
      bl_mdt: moment().format('YYYY/MM/DD HH:mm:ss'),
      bl_details: [],
    }),
    [gdata.company, gdata.user, defOutlet],
  );

  const {...methods} = useForm<any>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  // Update outlet when default changes
  useEffect(() => {
    methods.setValue('bl_outlet', defOutlet);
  }, [defOutlet, methods]);

  const [insertPOEBill] = useInsertPOEBillMutation();
  const [updatePOEBill] = useUpdatePOEBillMutation();

  const onSubmit: SubmitHandler<po_ebill> = useCallback(
    async values => {
      if (isSaving) return;

      try {
        setIsSaving(true);

        if (Number(values.bl_id) !== 0) {
          await updatePOEBill(values).unwrap();
          methods.reset();
          ShowToast('Data Updated Successfully');
        } else {
          await insertPOEBill(values).unwrap();
          methods.reset();
          ShowToast('Data Saved Successfully');
        }

        setIsSaving(false);
        navigation.navigate('Tables');
      } catch (e: any) {
        ShowToast(e?.data?.message || e?.message || 'An error occurred', 'error');
        setIsSaving(false);
      }
    },
    [isSaving, updatePOEBill, insertPOEBill, methods, navigation],
  );

  // Process bill data and update form
  useEffect(() => {
    if (!billData || billData.length === 0) {
      methods.reset(initialValues);
      setBillfooter({
        stotqty: '0.00',
        stotamt: '0.00',
        totdisc: '0.00',
        tottax: '0.00',
        roff: '0.00',
        ntotamt: '0.00',
      });
      return;
    }

    let tx = 0;
    const dets: any[] = billData.map((v: any, i: number) => {
      const amt = Number(v.od_rate) * Number(v.od_qty);
      const txper =
        Number(v.od_tax1per) + Number(v.od_tax2per) + Number(v.od_tax3per);
      tx += round((amt * txper) / 100, 2);

      return {
        bd_id: 0,
        bd_srid: i + 1,
        bd_prod: v.od_prod,
        bd_descr: v.od_descr,
        bd_rate: v.od_rate,
        bd_qty: v.od_qty,
        bd_tax1: v.od_tax1,
        bd_tax1per: v.od_tax1per,
        bd_tax1amt: v.od_tax1amt,
        bd_tax2: v.od_tax2,
        bd_tax2per: v.od_tax2per,
        bd_tax2amt: v.od_tax2amt,
        bd_tax3: v.od_tax3,
        bd_tax3per: v.od_tax3per,
        bd_tax3amt: v.od_tax3amt,
        bd_discper: v.od_discper,
        bd_discamt: v.od_discamt,
      };
    });

    const bData = billData[0];
    const totalWithTax = totals.totAmt + tx;

    methods.reset({
      bl_id: 0,
      bl_company: gdata.company,
      bl_outlet: defOutlet,
      bl_date: new Date(),
      bl_no: '',
      bl_checkin: bData?.or_checkin,
      bl_table: bData?.or_table,
      bl_tablename: '',
      bl_waiter: bData?.or_waiter,
      bl_pax: bData?.or_pax,
      bl_mobile: bData?.or_mobile,
      bl_name: bData?.or_name,
      bl_address: bData?.or_address,
      bl_kotids: bData?.or_kotids,
      bl_kotnos: bData?.or_kotnos,
      bl_roff: (round(totalWithTax, 0) - totalWithTax).toFixed(2),
      bl_amt: round(totalWithTax, 0).toFixed(2),
      bl_flg: 0,
      bl_uid: gdata.user,
      bl_cdt: new Date(),
      bl_muid: gdata.user,
      bl_mdt: new Date(),
      bl_details: dets,
    });

    setBillfooter({
      stotqty: totals.totQty.toString(),
      stotamt: totals.totAmt.toFixed(2),
      totdisc: '0.00',
      tottax: round(tx, 2).toFixed(2),
      roff: (round(totalWithTax, 0) - totalWithTax).toFixed(2),
      ntotamt: round(totalWithTax, 0).toFixed(2),
    });
  }, [
    billData,
    totals,
    gdata.company,
    gdata.user,
    defOutlet,
    methods,
    initialValues,
  ]);

  // Format tables data for display
  const tables = useMemo(() => {
    return (
      data
        ?.filter(v => v.tb_status !== null)
        ?.map(value => ({
          tb_name: value.tb_name,
          tb_date: moment(value.tb_date).format('DD-MM-YY hh:mm'),
          tb_waiter: value.tb_waiter,
          tb_id: value.tb_id,
          tb_status: value.tb_status,
          tb_capcity: value.tb_capcity,
          value: value,
        })) || []
    );
  }, [data]);

  // Format tables for dropdown
  const tables1 = useMemo(() => {
    return (
      data?.map(value => ({
        label: value.tb_name,
        id: value.tb_id,
        table: value,
      })) || []
    );
  }, [data]);

  // Format waiters for dropdown
  const waiters = useMemo(() => {
    return (
      employeeData?.map(value => ({
        label: value.em_name,
        id: value.em_id,
        table: value,
      })) || []
    );
  }, [employeeData]);

  // Handle table click
  const handleTableClick = useCallback(
    (table: any) => {
      setBillQry({cid: gdata.company, tbl: table.tb_id, value: table});
    },
    [gdata.company],
  );

  // Render separator for table list
  const renderSeparator = useCallback(
    () => <View style={styles.separator} />,
    [],
  );

  // Render table item
  const renderItem = useCallback(
    ({item}: any) => {
      if (!item) return <View />;

      return (
        <TouchableOpacity
          onPress={() => handleTableClick(item.value)}
          key={item.tb_id}
          style={styles.itemContainer}>
          <View style={styles.conLstItem}>
            <View style={styles.conImg}>
              <View
                style={
                  item?.tb_status === null ? styles.avatar : styles.avatar1
                }>
                <Text style={styles.txt}>{item?.tb_name}</Text>
              </View>
            </View>
            <View style={styles.conLstData}>
              <Text style={styles.heading}>{`Pax ~ ${item?.tb_capcity}`}</Text>
              <Text style={styles.descr}>{item?.tb_waitername}</Text>
            </View>
          </View>
        </TouchableOpacity>
      );
    },
    [handleTableClick],
  );

  // Get bill details data for split bill
  const getBillDetailsData = useCallback(() => {
    if (billData && billData.length > 0) {
      return billData;
    }
    return [];
  }, [billData]);

  // Handle split bill click
  const handleSplitBill = useCallback(() => {
    const currentDetails = getBillDetailsData();
    if (!currentDetails || currentDetails.length === 0) {
      ShowToast('No items to split', 'error');
      return;
    }
    setIsSplitBillOpen(true);
    setSplitBills([[]]); // Start with one empty split bill
  }, [getBillDetailsData]);

  // Handle confirm split
  const handleConfirmSplit = useCallback(async () => {
    if (isSaving) return;

    const items = getBillDetailsData();

    // Check if all items are assigned
    const totalAssignedItems = splitBills.reduce(
      (sum, bill) => sum + bill.length,
      0,
    );
    if (totalAssignedItems !== items.length) {
      ShowToast(
        `Please assign all items to split bills (${totalAssignedItems} of ${items.length} assigned)`,
        'error',
      );
      return;
    }

    if (splitBills.length === 0) {
      ShowToast('Please create at least one split bill', 'error');
      return;
    }

    // Check if any split bill has no items
    const emptyBills = splitBills.filter(bill => bill.length === 0);
    if (emptyBills.length > 0) {
      ShowToast('Some split bills have no items assigned', 'error');
      return;
    }

    setIsSaving(true);

    try {
      // Create bills for each split
      for (let i = 0; i < splitBills.length; i++) {
        const splitBillItems = splitBills[i];

        if (splitBillItems.length === 0) continue;

        let splitTotAmt = 0;
        let tx = 0,
          tx1 = 0,
          tx2 = 0,
          tx3 = 0,
          totDisc = 0;

        const splitDetails = splitBillItems.map((item: any, idx: number) => {
          const itemQty = Number(item.od_qty);
          const itemRate = Number(item.od_rate);
          const amt = round(itemQty * itemRate, 2);

          splitTotAmt += amt;

          const tax1per = Number(item.od_tax1per) || 0;
          const tax2per = Number(item.od_tax2per) || 0;
          const tax3per = Number(item.od_tax3per) || 0;

          let discAmt = 0; // No discount for split bills
          totDisc += discAmt;
          let afterDisc = amt - discAmt;

          let taxAmt = (afterDisc * (tax1per + tax2per + tax3per)) / 100;
          tx += taxAmt;
          tx1 += (afterDisc * tax1per) / 100;
          tx2 += (afterDisc * tax2per) / 100;
          tx3 += (afterDisc * tax3per) / 100;

          return {
            bd_id: 0,
            bd_srid: idx + 1,
            bd_prod: item.od_prod,
            bd_descr: item.od_descr,
            bd_rate: itemRate,
            bd_qty: itemQty,
            bd_tax1: item.od_tax1 || 1,
            bd_tax1per: tax1per,
            bd_tax1amt: round((afterDisc * tax1per) / 100, 2),
            bd_tax2: item.od_tax2 || 2,
            bd_tax2per: tax2per,
            bd_tax2amt: round((afterDisc * tax2per) / 100, 2),
            bd_tax3: item.od_tax3 || 3,
            bd_tax3per: tax3per,
            bd_tax3amt: round((afterDisc * tax3per) / 100, 2),
            bd_discper: 0,
            bd_discamt: round(discAmt, 2),
            bd_amt: round(
              afterDisc + (afterDisc * (tax1per + tax2per + tax3per)) / 100,
              2,
            ),
          };
        });

        tx = Number(tx.toFixed(2));
        tx1 = Number(tx1.toFixed(2));
        tx2 = Number(tx2.toFixed(2));
        tx3 = Number(tx3.toFixed(2));
        totDisc = Number(totDisc.toFixed(2));

        const netTotal = splitTotAmt - totDisc + tx;
        const roundedTotal = round(netTotal, 0);
        const roundOff = (roundedTotal - netTotal).toFixed(2);

        const splitBillData: any = {
          bl_id: 0,
          bl_company: gdata.company,
          bl_outlet: defOutlet,
          bl_date:
            moment(gdata.today).format('YYYY/MM/DD') +
            moment().format(' HH:mm:ss'),
          bl_no: '',
          bl_checkin: methods.getValues('bl_checkin') || 0,
          bl_table: methods.getValues('bl_table') || 0,
          bl_tablename: '',
          bl_waiter: methods.getValues('bl_waiter') || 0,
          bl_pax: methods.getValues('bl_pax') || 0,
          bl_mobile: methods.getValues('bl_mobile') || '0000000000',
          bl_name: methods.getValues('bl_name') || '',
          bl_address: methods.getValues('bl_address') || '',
          bl_tax1no: methods.getValues('bl_tax1no') || '',
          bl_kotids: methods.getValues('bl_kotids') || '',
          bl_kotnos: methods.getValues('bl_kotnos') || '',
          bl_roff: Number(roundOff) || 0,
          bl_amt: Number(roundedTotal.toFixed(2)) || 0,
          bl_flg: 0,
          bl_uid: gdata.user,
          bl_cdt: moment().format('YYYY/MM/DD HH:mm:ss'),
          bl_muid: gdata.user,
          bl_mdt: moment().format('YYYY/MM/DD HH:mm:ss'),
          bl_details: splitDetails,
          bl_mode: methods.getValues('bl_mode') || -1,
          bl_issplit: 1,
        };

        await insertPOEBill(splitBillData).unwrap();
      }

      ShowToast(`Bill split into ${splitBills.length} separate bills`);
      setIsSplitBillOpen(false);
      setSplitBills([[]]);
      methods.reset();
      navigation.navigate('Tables');
    } catch (error: any) {
      console.error('Error splitting bill:', error);
      ShowToast(error?.data?.message || error?.message || 'Failed to split bill', 'error');
      setIsSaving(false);
    }
  }, [
    isSaving,
    splitBills,
    getBillDetailsData,
    gdata.company,
    gdata.user,
    defOutlet,
    methods,
    insertPOEBill,
    navigation,
  ]);

  // Handle add split bill
  const handleAddSplitBill = useCallback(() => {
    setSplitBills([...splitBills, []]);
  }, [splitBills]);

  // Handle remove split bill
  const handleRemoveSplitBill = useCallback(
    (index: number) => {
      if (splitBills.length <= 1) {
        ShowToast('At least one split bill is required', 'error');
        return;
      }
      const newSplitBills = splitBills.filter((_, i) => i !== index);
      setSplitBills(newSplitBills);
    },
    [splitBills],
  );

  // Handle toggle item in split
  const handleToggleItemInSplit = useCallback(
    (itemIndex: number, splitIndex: number) => {
      const items = getBillDetailsData();
      const item = items[itemIndex];
      const newSplitBills = [...splitBills];

      // Check if item is in this split by comparing object references
      const itemInSplit = newSplitBills[splitIndex].some(
        (billItem: any) => billItem === item,
      );

      if (itemInSplit) {
        // Remove from split
        newSplitBills[splitIndex] = newSplitBills[splitIndex].filter(
          (billItem: any) => billItem !== item,
        );
      } else {
        // First remove from any other split (move from one split to another)
        for (let i = 0; i < newSplitBills.length; i++) {
          if (i !== splitIndex) {
            newSplitBills[i] = newSplitBills[i].filter(
              (billItem: any) => billItem !== item,
            );
          }
        }
        // Add to this split
        newSplitBills[splitIndex].push(item);
      }

      setSplitBills(newSplitBills);
    },
    [splitBills, getBillDetailsData],
  );

  const isLoading = isLoadingTables || isLoadingOutlets || isLoadingEmployees;

  return (
    <View style={{flex: 1}}>
      {/* Header - Fixed outside ScrollView */}
      <View style={styles.modernHeaderContainer}>
        <Text style={styles.modernHeader}><T>Billing Entry</T></Text>
      </View>

      <ScrollView
        keyboardShouldPersistTaps="handled"
        style={styles.app}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: 80,
        }}
        showsVerticalScrollIndicator={true}>
          {/* Loading State */}
          {isLoading && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#6B46C1" />
              <Text style={styles.loadingText}><T>Loading...</T></Text>
            </View>
          )}

          {/* Tables List */}
          {!isLoading && tables?.length === 0 && billData?.length === 0 && (
            <View style={styles.modernCard}>
              <Text style={styles.emptyStateText}><T>No tables available</T></Text>
            </View>
          )}

          {!isLoading && billData?.length === 0 && tables?.length > 0 && (
            <View style={styles.modernCard}>
              <Text style={styles.sectionTitle}><T>Select Table</T></Text>
              <FlatList
                ItemSeparatorComponent={renderSeparator}
                data={tables}
                renderItem={renderItem}
                onEndReachedThreshold={100}
                numColumns={5}
                style={styles.tablesGrid}
                keyboardShouldPersistTaps="handled"
              />
            </View>
          )}
          {/* Bill Form */}
          {(billData?.length > 0 || !isLoading) && (
            <View style={styles.modernCard}>
              <FormProvider {...methods}>
                {/* Table and Waiter Selection */}
                <Row style={styles.formRow}>
                  <Col>
                    <BTComboBox
                      name="bl_table"
                      label="Table"
                      data={tables1}
                      placeholder={tp("Table No")}
                      disabled
                    />
                  </Col>
                  <Col>
                    <BTComboBox
                      name="bl_waiter"
                      label="Waiter"
                      data={waiters}
                      placeholder={tp("Waiter Name")}
                    />
                  </Col>
                </Row>

                {/* Pax and Mobile */}
                <Row style={styles.formRow}>
                  <Col>
                    <TextInput
                      name="bl_pax"
                      label="Pax"
                      placeholder={tp("Enter pax")}
                      keyboardType="numeric"
                    />
                  </Col>
                  <Col>
                    <TextInput
                      name="bl_mobile"
                      label="Mobile"
                      placeholder={tp("Enter mobile")}
                      keyboardType="numeric"
                    />
                  </Col>
                </Row>

                {/* Bill Details Header */}
                {billData?.length > 0 && (
                  <View style={styles.billSection}>
                    <View style={styles.billHeader}>
                      <View style={{flex: 2}}>
                        <Text style={styles.billHeaderText}><T>Description</T></Text>
                      </View>
                      <View style={{width: 70, alignItems: 'flex-end'}}>
                        <Text style={styles.billHeaderText}><T>Rate</T></Text>
                      </View>
                      <View style={{width: 50, alignItems: 'center'}}>
                        <Text style={styles.billHeaderText}><T>Qty</T></Text>
                      </View>
                      <View style={{width: 90, alignItems: 'flex-end'}}>
                        <Text style={styles.billHeaderText}><T>Amount</T></Text>
                      </View>
                    </View>

                    {/* Bill Items */}
                    <View style={styles.billItemsContainer}>{billDetails}</View>
                  </View>
                )}
                {/* Bill Summary */}
                {billData?.length > 0 && (
                  <View style={styles.billSummaryContainer}>
                    <View style={styles.billSummaryRow}>
                      <Text style={styles.billSummaryLabel}><T>Sub Total</T></Text>
                      <Text style={styles.billSummaryValue}>
                        {billfooter.stotamt}
                      </Text>
                    </View>
                    <View style={styles.billSummaryRow}>
                      <Text style={styles.billSummaryLabel}><T>Discount</T></Text>
                      <Text style={styles.billSummaryValue}>
                        {billfooter.totdisc}
                      </Text>
                    </View>
                    <View style={styles.billSummaryRow}>
                      <Text style={styles.billSummaryLabel}><T>Tax</T></Text>
                      <Text style={styles.billSummaryValue}>
                        {billfooter.tottax}
                      </Text>
                    </View>
                    <View style={styles.billSummaryRow}>
                      <Text style={styles.billSummaryLabel}><T>Round Off</T></Text>
                      <Text style={styles.billSummaryValue}>
                        {billfooter.roff}
                      </Text>
                    </View>
                    <View style={styles.billTotalRow}>
                      <Text style={styles.billTotalLabel}><T>Total</T></Text>
                      <Text style={styles.billTotalValue}>
                        {billfooter.ntotamt}
                      </Text>
                    </View>
                  </View>
                )}
                {/* Action Buttons */}
                {billData?.length > 0 && (
                  <View style={buttonStyles.buttonRowContainer}>
                    <Row>
                      <Col>
                        <TouchableOpacity
                          style={buttonStyles.iconButton}
                          onPress={methods.handleSubmit(onSubmit)}
                          disabled={isSaving}>
                          <Icon name="print" size={20} color={colors.white} />
                          <Text style={buttonStyles.iconButtonText}><T>Print</T></Text>
                        </TouchableOpacity>
                      </Col>
                      <Col>
                        <TouchableOpacity
                          style={buttonStyles.iconButton}
                          onPress={() => methods.reset()}
                          disabled={isSaving}>
                          <Icon name="refresh" size={20} color={colors.white} />
                          <Text style={buttonStyles.iconButtonText}><T>Reset</T></Text>
                        </TouchableOpacity>
                      </Col>
                      <Col>
                        <TouchableOpacity
                          style={buttonStyles.iconButton}
                          onPress={() =>
                            navigation.navigate('Products', {
                              table: billQry.value,
                            })
                          }
                          disabled={isSaving}>
                          <Icon
                            name="plus-circle"
                            size={20}
                            color={colors.white}
                          />
                          <Text style={buttonStyles.iconButtonText}><T>Order</T></Text>
                        </TouchableOpacity>
                      </Col>
                      {gdata.menu?.find(v => v.mn_link === '/transaction/pokot')
                        ?.um_modify === '1' && (
                        <Col>
                          <TouchableOpacity
                            style={buttonStyles.iconButton}
                            onPress={() =>
                              navigation.navigate('CancelKOT', {
                                table: billQry.value,
                              })
                            }
                            disabled={isSaving}>
                            <Icon
                              name="times-circle"
                              size={20}
                              color={colors.white}
                            />
                            <Text style={buttonStyles.iconButtonText}>
                              <T>Cancel</T>
                            </Text>
                          </TouchableOpacity>
                        </Col>
                      )}
                      <Col>
                        <TouchableOpacity
                          style={buttonStyles.iconButton}
                          onPress={handleSplitBill}
                          disabled={isSaving}>
                          <Icon
                            name="scissors"
                            size={20}
                            color={colors.white}
                          />
                          <Text style={buttonStyles.iconButtonText}><T>Split</T></Text>
                        </TouchableOpacity>
                      </Col>
                    </Row>
                  </View>
                )}
              </FormProvider>
            </View>
          )}

        {/* Split Bill Modal */}
        <Modal
          visible={isSplitBillOpen}
          animationType="slide"
          transparent={true}
          onRequestClose={() => setIsSplitBillOpen(false)}>
          <View
            style={{
              flex: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 20,
            }}>
            <View
              style={{
                backgroundColor: 'white',
                borderRadius: 10,
                width: '100%',
                maxHeight: '90%',
                padding: 20,
              }}>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 20,
                }}>
                <Text style={{fontSize: 20, fontWeight: 'bold', color: '#333'}}>
                  <T>Split Bill</T>
                </Text>
                <TouchableOpacity
                  onPress={() => setIsSplitBillOpen(false)}
                  disabled={isSaving}>
                  <Icon name="times" size={24} color="#666" />
                </TouchableOpacity>
              </View>

              <ScrollView
                style={{maxHeight: '70%'}}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
                keyboardShouldPersistTaps="handled">
                {/* Available Items */}
                <View style={{marginBottom: 20}}>
                  <Text
                    style={{
                      fontSize: 16,
                      fontWeight: 'bold',
                      marginBottom: 10,
                    }}>
                    <T>All Items (</T>{getBillDetailsData().length})
                  </Text>
                  <ScrollView
                    style={{
                      backgroundColor: '#f5f5f5',
                      borderRadius: 8,
                      padding: 10,
                      maxHeight: 200,
                    }}
                    nestedScrollEnabled={true}
                    showsVerticalScrollIndicator={true}>
                    {getBillDetailsData().map(
                      (item: any, itemIndex: number) => {
                        const isItemAssigned = splitBills.some(bill =>
                          bill.some((billItem: any) => billItem === item),
                        );
                        return (
                          <View
                            key={itemIndex}
                            style={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              paddingVertical: 8,
                              borderBottomWidth: 1,
                              borderBottomColor: '#e0e0e0',
                            }}>
                            <View style={{flex: 1}}>
                              <Text style={{fontWeight: 'bold', fontSize: 14}}>
                                {item.od_descr}
                              </Text>
                              <Text style={{fontSize: 12, color: '#666'}}>
                                <T>Qty:</T> {item.od_qty} × ₹{item.od_rate} = ₹
                                {round(item.od_qty * item.od_rate, 2).toFixed(
                                  2,
                                )}
                              </Text>
                            </View>
                            <View
                              style={{
                                backgroundColor: isItemAssigned
                                  ? '#4CAF50'
                                  : '#ccc',
                                paddingHorizontal: 8,
                                paddingVertical: 4,
                                borderRadius: 4,
                              }}>
                              <Text
                                style={{
                                  color: 'white',
                                  fontSize: 12,
                                  fontWeight: 'bold',
                                }}>
                                {isItemAssigned ? 'Assigned' : 'Available'}
                              </Text>
                            </View>
                          </View>
                        );
                      },
                    )}
                  </ScrollView>
                </View>

                {/* Split Bills */}
                <View>
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 10,
                    }}>
                    <Text style={{fontSize: 16, fontWeight: 'bold'}}>
                      <T>Split Bills (</T>{splitBills.length})
                    </Text>
                    <TouchableOpacity
                      onPress={handleAddSplitBill}
                      style={{
                        backgroundColor: '#6B46C1',
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                        borderRadius: 6,
                      }}>
                      <View
                        style={{flexDirection: 'row', alignItems: 'center'}}>
                        <Icon name="plus" size={14} color="white" />
                        <Text
                          style={{
                            color: 'white',
                            marginLeft: 4,
                            fontSize: 12,
                          }}>
                          <T>Add Split</T>
                        </Text>
                      </View>
                    </TouchableOpacity>
                  </View>

                  <View>
                    {splitBills.map((splitBill, splitIndex) => {
                      return (
                        <View
                          key={splitIndex}
                          style={{
                            width: '100%',
                            backgroundColor: '#f9f9f9',
                            borderRadius: 8,
                            padding: 12,
                            borderWidth: 2,
                            borderColor:
                              splitBill.length > 0 ? '#4CAF50' : '#e0e0e0',
                            marginBottom: 12,
                          }}>
                          <View
                            style={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: 10,
                            }}>
                            <Text
                              style={{
                                fontSize: 14,
                                fontWeight: 'bold',
                                color: '#6B46C1',
                              }}>
                              <T>Bill</T> {splitIndex + 1}
                            </Text>
                            {splitBills.length > 1 && (
                              <TouchableOpacity
                                onPress={() =>
                                  handleRemoveSplitBill(splitIndex)
                                }>
                                <Icon
                                  name="times-circle"
                                  size={20}
                                  color="#EF4444"
                                />
                              </TouchableOpacity>
                            )}
                          </View>

                          <Text
                            style={{
                              fontSize: 12,
                              color: '#666',
                              marginBottom: 8,
                            }}>
                            <T>Items:</T> {splitBill.length}
                          </Text>

                          <ScrollView
                            style={{maxHeight: 120, marginBottom: 10}}
                            nestedScrollEnabled={true}>
                            {splitBill.map((item: any, idx: number) => (
                              <View
                                key={idx}
                                style={{
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                  paddingVertical: 4,
                                  borderBottomWidth: 1,
                                  borderBottomColor: '#e0e0e0',
                                }}>
                                <Text style={{fontSize: 11, flex: 1}}>
                                  {item.od_descr}
                                </Text>
                                <Text
                                  style={{fontSize: 11, fontWeight: 'bold'}}>
                                  <T>Qty:</T> {item.od_qty}
                                </Text>
                              </View>
                            ))}
                            {splitBill.length === 0 && (
                              <Text
                                style={{
                                  fontSize: 12,
                                  color: '#999',
                                  fontStyle: 'italic',
                                }}>
                                <T>No items selected</T>
                              </Text>
                            )}
                          </ScrollView>

                          {/* Item selection checkboxes */}
                          <ScrollView
                            style={{maxHeight: 150}}
                            nestedScrollEnabled={true}>
                            <Text
                              style={{
                                fontSize: 11,
                                color: '#666',
                                marginBottom: 5,
                                fontWeight: 'bold',
                              }}>
                              <T>Select Items:</T>
                            </Text>
                            {getBillDetailsData().map(
                              (item: any, itemIndex: number) => {
                                const isItemInThisSplit = splitBill.some(
                                  (billItem: any) => billItem === item,
                                );
                                const isItemInOtherSplit = splitBills.some(
                                  (otherBill: any, otherIndex: number) =>
                                    otherIndex !== splitIndex &&
                                    otherBill.some(
                                      (billItem: any) => billItem === item,
                                    ),
                                );

                                return (
                                  <TouchableOpacity
                                    key={itemIndex}
                                    onPress={() =>
                                      handleToggleItemInSplit(
                                        itemIndex,
                                        splitIndex,
                                      )
                                    }
                                    style={{
                                      flexDirection: 'row',
                                      alignItems: 'center',
                                      paddingVertical: 6,
                                      opacity: isItemInOtherSplit ? 0.5 : 1,
                                    }}>
                                    <Icon
                                      name={
                                        isItemInThisSplit
                                          ? 'check-square-o'
                                          : 'square-o'
                                      }
                                      size={16}
                                      color={
                                        isItemInThisSplit ? '#4CAF50' : '#666'
                                      }
                                    />
                                    <Text
                                      style={{
                                        marginLeft: 8,
                                        fontSize: 11,
                                        flex: 1,
                                      }}>
                                      {item.od_descr}
                                    </Text>
                                  </TouchableOpacity>
                                );
                              },
                            )}
                          </ScrollView>
                        </View>
                      );
                    })}
                  </View>
                </View>
              </ScrollView>

              {/* Modal Actions */}
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  marginTop: 20,
                  paddingTop: 20,
                  borderTopWidth: 1,
                  borderTopColor: '#e0e0e0',
                }}>
                <TouchableOpacity
                  onPress={() => setIsSplitBillOpen(false)}
                  disabled={isSaving}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 10,
                    marginRight: 10,
                    borderRadius: 6,
                    backgroundColor: '#e0e0e0',
                  }}>
                  <Text style={{color: '#333', fontWeight: 'bold'}}>
                    <T>Cancel</T>
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={handleConfirmSplit}
                  disabled={isSaving}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 10,
                    borderRadius: 6,
                    backgroundColor: '#6B46C1',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}>
                  {isSaving && (
                    <ActivityIndicator
                      size="small"
                      color="white"
                      style={{marginRight: 8}}
                    />
                  )}
                  <Icon name="scissors" size={14} color="white" />
                  <Text
                    style={{
                      color: 'white',
                      fontWeight: 'bold',
                      marginLeft: 6,
                    }}>
                    {isSaving ? 'Saving...' : 'Confirm Split'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </ScrollView>
    </View>
  );
};

export default POSSBill;

